import type { TimelineStep } from "../components/ApprovalTimeline";
import type { RequestDetail } from "../services/api";

/** AWST display (UTC at rest per CLAUDE.md). */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
}

const DONE_REQUEST = new Set(["approved", "auto_approved", "complete", "completed"]);
const DONE_STEP = new Set(["approved", "auto_approved", "complete", "completed", "skipped"]);

/**
 * Adapt the live RequestDetail into the numbered-node timeline shape. Each step
 * names its effective approver (post-delegation) and its state, so the UI can
 * always show who holds it and what's next (Crystal Comms).
 */
export function requestTimeline(detail: RequestDetail): TimelineStep[] {
  const current = detail.current_step_index;
  const requestDone = DONE_REQUEST.has(detail.state);
  return [...detail.steps]
    .sort((a, b) => a.step_index - b.step_index)
    .map((step) => {
      const decided = step.assignments.find((a) => a.decision);
      const holder = step.assignments[0];
      const name = holder?.effective_approver_name ?? step.step_name ?? "Awaiting assignment";

      let state: TimelineStep["state"];
      if (step.state === "rejected" || decided?.decision === "rejected") {
        state = "rejected";
      } else if (DONE_STEP.has(step.state) || (requestDone && step.state !== "rejected")) {
        state = "done";
      } else if (
        current !== null &&
        step.step_index === current &&
        (detail.state === "pending" || detail.state === "submitted")
      ) {
        state = "current";
      } else {
        state = "todo";
      }

      let at: string | null = null;
      if (state === "done" || state === "rejected") {
        at = decided?.decided_at ? fmtDateTime(decided.decided_at) : null;
      } else if (state === "current") {
        at = step.started_at ? `Assigned ${fmtDateTime(step.started_at)}` : "Assigned just now";
      }

      const delegated =
        holder &&
        holder.original_approver_user_id &&
        holder.original_approver_user_id !== holder.effective_approver_user_id
          ? holder.original_approver_name
          : null;

      return {
        role: step.step_name,
        name: delegated ? `${name} (delegated from ${delegated})` : name,
        state,
        at,
        note: state === "rejected" ? decided?.decision_comment ?? null : null,
      };
    });
}

/**
 * Crystal-Comms next-action line for a request: always names the holder and the
 * next step, never a bare "Pending".
 */
export function nextActionLine(detail: RequestDetail): string {
  const ticket = detail.linked_autotask_ticket_number;
  switch (detail.state) {
    case "pending": {
      const cur = detail.steps.find((s) => s.step_index === detail.current_step_index);
      const holder = cur?.assignments[0]?.effective_approver_name;
      const role = cur?.step_name;
      if (holder) {
        return `Now with ${holder}${role ? ` for ${role} approval` : ""}. Nudge fires in 2 business days; the request expires after 6 if no action.`;
      }
      return "Awaiting approval. You'll see the holder as soon as the step is assigned.";
    }
    case "submitted":
      return "Received by the iQuest service desk. We'll confirm the next step shortly.";
    case "approved":
      return ticket
        ? `Approved. iQuest is provisioning this against ticket ${ticket}.`
        : "Approved. iQuest is now actioning the request.";
    case "auto_approved":
      return ticket
        ? `Auto-approved (you hold the approving authority). Linked ticket ${ticket}.`
        : "Auto-approved (you hold the approving authority). iQuest is actioning it.";
    case "rejected": {
      const rej = [...detail.steps]
        .reverse()
        .flatMap((s) => s.assignments)
        .find((a) => a.decision === "rejected");
      const by = rej?.effective_approver_name;
      const reason = detail.rejection_reason ?? rej?.decision_comment;
      return `Rejected${by ? ` by ${by}` : ""}${reason ? ` — "${reason}"` : "."}`;
    }
    case "expired":
      return "Expired without a decision. Submit a new request if it's still needed.";
    case "cancelled":
      return "Cancelled. Nothing further will happen on this request.";
    default:
      return "";
  }
}
